import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ModalService } from '@shopping-angular-components/modal';
import { SwipeToBuy, TripProductType, WizumenModel } from '../components/wizumen/wizumen-models';
import { LandingModelService } from './landingModel.service';
import { SwipeToBuyModal } from '../components/wizumen/swipe-to-buy-modal/swipe-to-buy-modal.component';
import { UpaService, UpaTarget, UpaTrackingData } from './upa.service';
import { LoaderComponent } from '@shopping-angular-components/loader';
import { SwipeToBuyHeaderModal } from '../components/wizumen/swipe-to-buy-modal/swipe-to-buy-header-modal.component';
import { UtilsService } from './utils.service';
import { LandingModel } from '../model/landingModel';

type SwipeToBuyModalTrackingData = {
  moduleId: string;
  moduleIndex: number;
  uniqueId: string;
  flow: string;
}

declare var window: any;
@Injectable({
    providedIn: 'root'
})
export class SwipeToBuyService {
  assistanceModalId = "assistance-swipe-to-buy-modal"
  assistanceModalStorageKey = 'assistanceModalShownV3';
  assistanceModalModuleId = 'ASSISTANCE_MODAL';
  hotelModalStorageKey = 'hotelModalShown';
  landingModel: LandingModel;

  private swipeToBuy: SwipeToBuy;
  private daysBeforeTrip: number;
  private upaTrackingData: SwipeToBuyModalTrackingData;
  private assistanceDataURL = '/nymeria-gui/data/model/assistance';

  constructor(
    private httpClient: HttpClient,
    private upaService: UpaService,
    private modalService: ModalService,
    private landingModelService: LandingModelService,
    private utilsService: UtilsService) {
      this.landingModel = this.landingModelService.getLandingModel();
  }

  public openModal(upaTrackingData: SwipeToBuyModalTrackingData, swipeToBuy: SwipeToBuy, openedOnDemand: boolean = false): void {
    if(!swipeToBuy) return;
    this.daysBeforeTrip = swipeToBuy.days_before_trip;
    this.swipeToBuy = swipeToBuy;
    this.upaTrackingData = upaTrackingData;

    if (!this.hasToShow() && !openedOnDemand) return;

    this.showModal(this.upaTrackingData.flow, openedOnDemand);
  }

  public closeModal(label: string, forceClose: boolean = false, openedOnDemand: boolean = false, uniqueId: string = this.assistanceModalModuleId): void {
    this.trackClick(label, openedOnDemand);

    if (!openedOnDemand) {
      const date = new Date();
      const value = date.toISOString();
      date.setDate(date.getDate() + 360);
      const expires = "expires="+ date.toUTCString();
      const storageKey = this.getModalStorageKey();
      localStorage.setItem(storageKey, value);
      this.utilsService.setCookieValue(storageKey, value, expires);
    }

    if (forceClose) this.modalService.closeModal();
  }

  public async buy() {
    this.trackClick('buy', false);

    try {
      this.modalService.closeModal(true);
      LoaderComponent.showLoaderEvent.emit(true);
      const offerId = this.swipeToBuy.id;
      const country = this.landingModel.country;
      const language = this.landingModel.language || '';
      const channel = window?.upaData?.ch || '';
      const trackerId = window?.upaData?.userid || '';

      const url = `/nymeria-gui/data/model/trips/create/fromInsurance?insuranceId=${offerId}&countryCode=${country}&language=${language}&channel=${channel}&trackerId=${trackerId}`;
      const response = (await this.httpClient.get(url).toPromise() as any);
      const checkoutUrl = response.url;

      if (checkoutUrl) window.location.assign(checkoutUrl);
    } catch(e) {
      this.upaService.trackView({
        ...this.upaTrackingData,
        label: `Error creating trip with insuranceId: ${this.swipeToBuy.id}`,
        target: UpaTarget.SWIPE_TO_BUY_MODAL,
      });
    }

    LoaderComponent.showLoaderEvent.emit(false);
  }

  private trackView(openedOnDemand: string, uniqueId: string) {
    const loyalty = this.swipeToBuy.loyalty;
    const priceState = loyalty?.redemption?.summary == null
      ? "paid-assistance"
      : loyalty.redemption.summary.saved != "0" && loyalty.redemption.summary.to_pay == "0"
        ? "free-assistance" : "discount-assistance";


    const lastSeenDate = this.getLastSeenDate() ? "seen-again" : "first-seen";

    const daysBeforeTripFlag = `${this.daysBeforeTrip}-days-before-trip`;

    const redemptionMode = this.getRedemptionStatusForTracking();

    this.upaService.trackView({
      ...this.upaTrackingData,
      label: this.swipeToBuy.code,
      target: openedOnDemand ? UpaTarget.SWIPE_TO_BUY_MODAL_FROM_CTA : UpaTarget.SWIPE_TO_BUY_MODAL,
      flags: [
        lastSeenDate,
        daysBeforeTripFlag,
        redemptionMode,
        priceState
      ]
    });
  }

  private trackClick(label: string, openedOnDemand) {
    this.upaService.trackClickTopic({
      ...this.upaTrackingData,
      label,
      target: openedOnDemand ? UpaTarget.SWIPE_TO_BUY_MODAL_FROM_CTA : UpaTarget.SWIPE_TO_BUY_MODAL,
      flags: [
        this.getRedemptionStatusForTracking()
      ]
    })
  }

  private applyDynamicColor(): void {
    const backgroundColor = this.swipeToBuy.background_color
      ? `var(${this.swipeToBuy.background_color})`
      : `var(--brand-secondary-1)`;

    const styleId = `dynamic-modal-bg-style-${this.assistanceModalId}`;
    let styleTag = document.getElementById(styleId) as HTMLStyleElement;
    
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }
    styleTag.textContent = `
      #${this.assistanceModalId} .modal-content {
        background-color: ${backgroundColor};
      }
    `;
  }

  private showModal(flow: string, openedOnDemand): void {
    const isHotelCoupon = this.swipeToBuy.product === TripProductType.HOTEL;
    if (isHotelCoupon) {
      this.applyDynamicColor();
    }

    this.modalService.showModal({
      id: this.assistanceModalId,
      product: "nymeria-gui",
      flow: flow,
      isWebview: this.landingModel.webview,
      headerComponent: SwipeToBuyHeaderModal,
      contentComponent: SwipeToBuyModal,
      headerModel: {
        partner: this.landingModel.partner,
        pictureMediaKey: this.swipeToBuy.destination_media_key
      },
      contentModel: {
        assistance: this.swipeToBuy,
        ...this.upaTrackingData
      },
      size: "-sm",
      forceHeader: true,
      openCallback: () => {
        this.trackView(openedOnDemand, this.upaTrackingData.uniqueId);
      },
      closeCallback: () => this.closeModal("close", false, openedOnDemand, this.upaTrackingData.uniqueId),
      closeOutsideCallback: () => this.closeModal("click-outside-modal", false, openedOnDemand, this.upaTrackingData.uniqueId),
      closeOnOutsideClick: true,
      evaClasses: isHotelCoupon ? "swipe-to-buy-modal hotel-coupon-modal" : "swipe-to-buy-modal",
      useFragmentNavigation: !this.landingModel.webview
    })
  }

  private getLastSeenDate() {
    return this.utilsService.getLocalStorageOrCookieValue(this.getModalStorageKey());
  }

  getModalStorageKey(): string {
    return this.swipeToBuy.product === TripProductType.HOTEL ? this.hotelModalStorageKey : this.assistanceModalStorageKey;
  }

  private isLoyaltyRedemptionEnabled(): boolean {
    return this.utilsService.getLocalStorageOrCookieValue("redemption_switch") == "redemption";
  }

  private getRedemptionStatusForTracking(): string {
    return this.isLoyaltyRedemptionEnabled() ? "redemption-enabled" : "redemption-disabled";
  }

  private hasToShow(): boolean {
    const lastSeen = this.getLastSeenDate();
    
    if (!lastSeen) {
      return true;
    }

    const lastSeenDate = new Date(lastSeen);
    const daysSinceLastSeen = this.calculateDaysSinceDate(lastSeenDate);

    if (this.swipeToBuy.product === TripProductType.HOTEL && typeof this.swipeToBuy.days_after_purchase === 'number') {
      return this.swipeToBuy.days_after_purchase < 7 ? daysSinceLastSeen >= 1 : daysSinceLastSeen >= 7;
    }

    return this.daysBeforeTrip <= 7 ? daysSinceLastSeen >= 1 : daysSinceLastSeen >= 7;
  }

  private calculateDaysSinceDate(date: Date): number {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    return Math.floor(diffInMs / (1000 * 3600 * 24));
  }

  async getAssistanceModal(params: { [key: string]: string }): Promise<SwipeToBuy> {

    const queryString = new URLSearchParams(params).toString();
    const url = `${this.assistanceDataURL}?${queryString}`;

    try {
      return await this.httpClient.get(url).toPromise() as SwipeToBuy;

    } catch (error: any) {
      throw new Error('Error fetching assistance modal: ' + error);
    }
  }
}
